import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Check, Eye, Search, X } from "lucide-react";
import { toast } from "sonner";

import DashboardHeader from "../components/DashboardHeader";

const api = `${process.env.REACT_APP_BACKEND_URL}/api/site-admin/catalog-review`;
const request = { withCredentials: true };

export default function ImportReviewPage({ user, onLogout }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState([]);
  const [detail, setDetail] = useState(null);
  const [preview, setPreview] = useState(null);
  const load = useCallback(async () => {
    try {
      const response = await axios.get(`${api}/candidates`, {
        ...request,
        params: { search: search || undefined, status: status || undefined },
      });
      setItems(response.data.items);
      setTotal(response.data.total);
    } catch {
      toast.error("Couldn’t load staged import candidates.");
    }
  }, [search, status]);
  useEffect(() => {
    load();
  }, [load]);
  const decide = async (candidateId, decision) => {
    const reason =
      decision === "approved_new"
        ? undefined
        : window.prompt("Reason for this decision:");
    if (decision !== "approved_new" && !reason) return;
    await axios.post(
      `${api}/candidates/${candidateId}/decision`,
      { decision, reason },
      request,
    );
    toast.success("Review decision recorded.");
    load();
    setDetail(null);
  };
  const bulk = async (decision) => {
    if (!selected.length) return;
    const reason =
      decision === "approved_new"
        ? undefined
        : window.prompt("Reason for this bulk decision:");
    if (decision !== "approved_new" && !reason) return;
    await axios.post(
      `${api}/bulk-decision`,
      {
        candidate_ids: selected,
        decision,
        reason,
        idempotency_key: `ui-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      },
      request,
    );
    setSelected([]);
    toast.success("Bulk review recorded.");
    load();
  };
  const showDetail = async (candidateId) =>
    setDetail(
      (await axios.get(`${api}/candidates/${candidateId}`, request)).data,
    );
  const showPreview = async () =>
    setPreview((await axios.get(`${api}/publish-preview`, request)).data);
  const counters = ["missing", "conflict", "existing", "reviewed"].map(
    (key) => [
      key,
      items.filter((item) => item.decision === key || item.status === key)
        .length,
    ],
  );
  return (
    <div className="review-shell" data-testid="import-review-page">
      <DashboardHeader user={user} activePage="Admin" onLogout={onLogout} />
      <main className="review-main">
        <header>
          <p className="eyebrow">Site administrator · staged imports</p>
          <h1>Import review queue</h1>
          <p>
            Review source-attributed candidates before any future publication.
          </p>
        </header>
        <section className="review-counters" aria-live="polite">
          {counters.map(([label, count]) => (
            <div key={label} data-testid={`review-counter-${label}`}>
              <span>{label}</span>
              <b>{count}</b>
            </div>
          ))}
        </section>
        <section className="review-toolbar">
          <label>
            <Search size={16} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search candidate name"
              data-testid="review-search-input"
            />
          </label>
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            data-testid="review-status-filter"
          >
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="reviewed">Reviewed</option>
          </select>
          <button
            onClick={() => bulk("approved_new")}
            data-testid="bulk-approve-button"
          >
            <Check size={15} /> Approve selected
          </button>
          <button
            onClick={() => bulk("rejected")}
            data-testid="bulk-reject-button"
          >
            <X size={15} /> Reject selected
          </button>
          <button onClick={showPreview} data-testid="publish-preview-button">
            Publish preview
          </button>
        </section>
        <p className="review-total" data-testid="review-total">
          {total} staged candidates
        </p>
        <div className="review-table-wrap">
          <table data-testid="review-candidate-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={
                      selected.length === items.length && items.length > 0
                    }
                    onChange={(event) =>
                      setSelected(
                        event.target.checked
                          ? items.map((item) => item.candidate_id)
                          : [],
                      )
                    }
                    data-testid="review-select-all"
                  />
                </th>
                <th>Name</th>
                <th>Language</th>
                <th>Classification</th>
                <th>Source</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.candidate_id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.includes(item.candidate_id)}
                      onChange={() =>
                        setSelected((current) =>
                          current.includes(item.candidate_id)
                            ? current.filter((id) => id !== item.candidate_id)
                            : [...current, item.candidate_id],
                        )
                      }
                      data-testid={`review-select-${item.candidate_id}`}
                    />
                  </td>
                  <td>{item.name}</td>
                  <td>{item.language}</td>
                  <td>{item.decision}</td>
                  <td>{item.source_kind}</td>
                  <td>{item.review_decision || item.status}</td>
                  <td>
                    <button
                      onClick={() => showDetail(item.candidate_id)}
                      data-testid={`review-detail-${item.candidate_id}`}
                    >
                      <Eye size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!items.length && (
            <div className="review-empty" data-testid="review-empty-state">
              No staged candidates match these filters.
            </div>
          )}
        </div>
      </main>
      {detail && (
        <aside className="review-drawer" data-testid="review-detail-drawer">
          <button
            onClick={() => setDetail(null)}
            data-testid="review-close-detail"
          >
            Close
          </button>
          <h2>{detail.candidate.name}</h2>
          <section>
            <h3>Current catalog</h3>
            <pre>{JSON.stringify(detail.current_catalog, null, 2)}</pre>
          </section>
          <section>
            <h3>Imported candidate</h3>
            <pre>{JSON.stringify(detail.candidate.raw, null, 2)}</pre>
          </section>
          <section>
            <h3>Source evidence</h3>
            <a
              href={detail.candidate.source_url}
              target="_blank"
              rel="noreferrer"
            >
              Official source
            </a>
            <pre>
              {JSON.stringify(detail.candidate.source_evidence, null, 2)}
            </pre>
          </section>
          <section>
            <h3>Audit history</h3>
            <pre>{JSON.stringify(detail.audit_history, null, 2)}</pre>
          </section>
          <button
            onClick={() =>
              decide(detail.candidate.candidate_id, "approved_new")
            }
            data-testid="review-approve-button"
          >
            Approve new record
          </button>
          <button
            onClick={() =>
              decide(detail.candidate.candidate_id, "keep_existing")
            }
            data-testid="review-keep-existing-button"
          >
            Keep existing
          </button>
          <button
            onClick={() => decide(detail.candidate.candidate_id, "rejected")}
            data-testid="review-reject-button"
          >
            Reject
          </button>
        </aside>
      )}
      {preview && (
        <div className="review-dialog" data-testid="publish-preview-dialog">
          <div>
            <h2>Publish preview</h2>
            <pre>{JSON.stringify(preview, null, 2)}</pre>
            <button
              onClick={() => setPreview(null)}
              data-testid="close-publish-preview"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
